const DB_NAME = 'audio-recorder';
const DB_STORE = 'recordings';
const DB_VERSION = 1;

function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            request.result.createObjectStore(DB_STORE);
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function getStorageKey(element: Element): string {
    const form = element.closest('form');
    const input = element.querySelector('input[type="file"]') as HTMLInputElement | null;
    const inputName = input?.name || 'default';
    const formAction = form?.action || window.location.pathname;

    return `${formAction}:${inputName}`;
}

async function saveBlob(key: string, blob: Blob, mimeType: string): Promise<void> {
    try {
        const db = await openDatabase();
        const tx = db.transaction(DB_STORE, 'readwrite');
        tx.objectStore(DB_STORE).put({blob, mimeType, timestamp: Date.now()}, key);
        await new Promise<void>((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
        db.close();
    } catch {
        // IndexedDB not available — silently ignore
    }
}

async function loadBlob(key: string): Promise<{blob: Blob; mimeType: string} | null> {
    try {
        const db = await openDatabase();
        const tx = db.transaction(DB_STORE, 'readonly');
        const request = tx.objectStore(DB_STORE).get(key);

        return new Promise((resolve) => {
            request.onsuccess = () => {
                db.close();
                const result = request.result;

                if (result && result.blob instanceof Blob) {
                    resolve({blob: result.blob, mimeType: result.mimeType});
                } else {
                    resolve(null);
                }
            };
            request.onerror = () => {
                db.close();
                resolve(null);
            };
        });
    } catch {
        return null;
    }
}

async function deleteBlob(key: string): Promise<void> {
    try {
        const db = await openDatabase();
        const tx = db.transaction(DB_STORE, 'readwrite');
        tx.objectStore(DB_STORE).delete(key);
        await new Promise<void>((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
        db.close();
    } catch {
        // Silently ignore
    }
}

export default class AudioRecorder {
    private readonly element: Element;
    private readonly fileInput: HTMLInputElement;
    private readonly startButton: HTMLButtonElement;
    private readonly stopButton: HTMLButtonElement;
    private readonly resetButton: HTMLButtonElement;
    private readonly timerElement: HTMLElement;
    private readonly previewContainer: HTMLElement;
    private readonly playerElement: HTMLAudioElement;
    private readonly errorElement: HTMLElement;
    private readonly storageKey: string;

    private mediaRecorder: MediaRecorder | null = null;
    private stream: MediaStream | null = null;
    private chunks: Blob[] = [];
    private timerInterval: ReturnType<typeof setInterval> | null = null;
    private recordingStartTime: number = 0;

    public constructor(element: Element) {
        this.element = element;
        this.fileInput = element.querySelector('input[type="file"]') as HTMLInputElement;
        this.startButton = element.querySelector('.audio-recorder__start') as HTMLButtonElement;
        this.stopButton = element.querySelector('.audio-recorder__stop') as HTMLButtonElement;
        this.resetButton = element.querySelector('.audio-recorder__reset') as HTMLButtonElement;
        this.timerElement = element.querySelector('.audio-recorder__timer') as HTMLElement;
        this.previewContainer = element.querySelector('.audio-recorder__preview') as HTMLElement;
        this.playerElement = element.querySelector('.audio-recorder__player') as HTMLAudioElement;
        this.errorElement = element.querySelector('.audio-recorder__error') as HTMLElement;
        this.storageKey = getStorageKey(element);

        this.startButton.addEventListener('click', () => this.startRecording());
        this.stopButton.addEventListener('click', () => this.stopRecording());
        this.resetButton.addEventListener('click', () => this.resetRecording());

        this.restoreRecording();

        const form = element.closest('form');

        if (form) {
            form.addEventListener('submit', () => {
                deleteBlob(this.storageKey);
            });
        }
    }

    private async startRecording(): Promise<void> {
        try {
            this.stream = await navigator.mediaDevices.getUserMedia({audio: true});
        } catch {
            this.showError('Přístup k mikrofonu byl zamítnut. Povolte přístup k mikrofonu v nastavení prohlížeče.');

            return;
        }

        this.hideError();
        this.chunks = [];

        const mimeType = this.getSupportedMimeType();
        const options: MediaRecorderOptions = mimeType ? {mimeType} : {};

        this.mediaRecorder = new MediaRecorder(this.stream, options);

        this.mediaRecorder.addEventListener('dataavailable', (event: BlobEvent) => {
            if (event.data.size > 0) {
                this.chunks.push(event.data);
            }
        });

        this.mediaRecorder.addEventListener('stop', () => {
            const actualMimeType = mimeType || 'audio/webm';
            const blob = new Blob(this.chunks, {type: actualMimeType});
            this.setFileFromBlob(blob, actualMimeType);
            saveBlob(this.storageKey, blob, actualMimeType);
        });

        this.mediaRecorder.start();
        this.startTimer();

        this.startButton.classList.add('d-none');
        this.stopButton.classList.remove('d-none');
        this.timerElement.classList.remove('d-none');
    }

    private stopRecording(): void {
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.stop();
        }

        this.releaseStream();
        this.stopTimer();

        this.stopButton.classList.add('d-none');
        this.timerElement.classList.add('d-none');
    }

    private resetRecording(): void {
        this.chunks = [];
        this.clearFileInput();

        this.previewContainer.classList.add('d-none');
        this.playerElement.src = '';
        this.startButton.classList.remove('d-none');
        this.stopButton.classList.add('d-none');
        this.timerElement.classList.add('d-none');
        this.timerElement.textContent = '00:00';
        this.hideError();
        this.releaseStream();
        this.stopTimer();
        deleteBlob(this.storageKey);
    }

    private async restoreRecording(): Promise<void> {
        const saved = await loadBlob(this.storageKey);

        if (saved === null) {
            return;
        }

        this.setFileFromBlob(saved.blob, saved.mimeType);
        this.startButton.classList.add('d-none');
    }

    private setFileFromBlob(blob: Blob, mimeType: string): void {
        const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
        const file = new File([blob], `recording.${extension}`, {type: mimeType});
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        this.fileInput.files = dataTransfer.files;

        const url = URL.createObjectURL(blob);
        this.playerElement.src = url;
        this.previewContainer.classList.remove('d-none');
    }

    private clearFileInput(): void {
        const dataTransfer = new DataTransfer();
        this.fileInput.files = dataTransfer.files;
    }

    private startTimer(): void {
        this.recordingStartTime = Date.now();
        this.timerElement.textContent = '00:00';

        this.timerInterval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - this.recordingStartTime) / 1000);
            const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0');
            const seconds = (elapsed % 60).toString().padStart(2, '0');
            this.timerElement.textContent = `${minutes}:${seconds}`;
        }, 1000);
    }

    private stopTimer(): void {
        if (this.timerInterval !== null) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
    }

    private releaseStream(): void {
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }
    }

    private getSupportedMimeType(): string | null {
        const types = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/mp4',
        ];

        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }

        return null;
    }

    private showError(message: string): void {
        this.errorElement.textContent = message;
        this.errorElement.classList.remove('d-none');
    }

    private hideError(): void {
        this.errorElement.classList.add('d-none');
        this.errorElement.textContent = '';
    }
}
